/**
 * Keyboard shortcuts (web.md §6): ONE keydown handler for the document and a registry areas add to.
 * Shortcuts are ignored while typing in a field and while any dialog is open; `mod+s` (Ctrl/Cmd + S) is
 * the exception that also works inside fields (it saves the settings editor).
 *
 *   useShortcut('j', () => next(), { description: 'Next message' })
 *
 * Keys are `KeyboardEvent.key` values, letters lower-case ('j', '1', '/', ',', '?', 'Escape'), or
 * 'mod+<key>'. When two registrations share a key, the most recent active one wins.
 */

import { useEffect, useRef } from 'react';
import { ui } from './ui-store.js';

export interface ShortcutOptions {
  description: string;
  /** Only active while this returns true. */
  when?: () => boolean;
}

interface Registration extends ShortcutOptions {
  key: string;
  handler: (event: KeyboardEvent) => void;
  order: number;
}

const registrations: Registration[] = [];
let order = 0;

export function registerShortcut(key: string, handler: (event: KeyboardEvent) => void, options: ShortcutOptions): () => void {
  const entry: Registration = { key: normaliseKey(key), handler, order: ++order, ...options };
  registrations.push(entry);
  return () => {
    const i = registrations.indexOf(entry);
    if (i >= 0) registrations.splice(i, 1);
  };
}

/** React binding: registered while mounted; always calls the latest handler. */
export function useShortcut(key: string, handler: (event: KeyboardEvent) => void, options: ShortcutOptions): void {
  const ref = useRef(handler);
  ref.current = handler;
  const whenRef = useRef(options.when);
  whenRef.current = options.when;
  useEffect(
    () => registerShortcut(key, (e) => ref.current(e), { description: options.description, when: () => (whenRef.current ? whenRef.current() : true) }),
    [key, options.description],
  );
}

function normaliseKey(key: string): string {
  if (key.startsWith('mod+')) return `mod+${key.slice(4).toLowerCase()}`;
  return key.length === 1 ? key.toLowerCase() : key;
}

/** True when the event comes from somewhere the user is typing. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return true;
  return false;
}

function anyDialogOpen(): boolean {
  return ui.get().dialogs.length > 0 || document.querySelector('dialog[open]') !== null;
}

function find(key: string): Registration | undefined {
  let best: Registration | undefined;
  for (const r of registrations) {
    if (r.key !== key) continue;
    if (r.when && !r.when()) continue;
    if (!best || r.order > best.order) best = r;
  }
  return best;
}

/** The document handler (exported for tests). Returns true when a shortcut ran. */
export function handleShortcut(event: KeyboardEvent): boolean {
  if (event.defaultPrevented) return false;
  if (event.ctrlKey || event.metaKey) {
    const r = find(`mod+${event.key.toLowerCase()}`);
    if (!r) return false;
    if (anyDialogOpen()) return false;
    event.preventDefault();
    r.handler(event);
    return true;
  }
  if (event.altKey) return false;
  if (isTypingTarget(event.target) || anyDialogOpen()) return false;
  const r = find(normaliseKey(event.key));
  if (!r) return false;
  event.preventDefault();
  r.handler(event);
  return true;
}

export function installShortcuts(target: Document = document): () => void {
  const listener = (event: KeyboardEvent): void => {
    handleShortcut(event);
  };
  target.addEventListener('keydown', listener);
  return () => target.removeEventListener('keydown', listener);
}

/** For the `?` list: active shortcuts, one per key. */
export function listShortcuts(): Array<{ key: string; description: string }> {
  const seen = new Map<string, Registration>();
  for (const r of registrations) {
    if (r.when && !r.when()) continue;
    const prev = seen.get(r.key);
    if (!prev || r.order > prev.order) seen.set(r.key, r);
  }
  return [...seen.values()].sort((a, b) => a.order - b.order).map((r) => ({ key: r.key, description: r.description }));
}
